import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, CheckCircle2, Phone, Award } from "lucide-react";
import { KIKI } from "@/data/kiki";
import HeroVideo from "@/components/landing/HeroVideo";
import { KineticHeadline, WordReveal } from "@/components/Kinetic";

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Subtle parallax + fade on scroll
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-18%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-8%"]);
  const opacityText = useTransform(scrollYProgress, [0, 0.9], [1, reduce ? 1 : 0.55]);
  const yVideo = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-12%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-20 sm:pt-28 lg:pt-32 pb-16 lg:pb-24 overflow-hidden bg-slate-50"
    >
      {/* Parallax backdrop */}
      <motion.div
        style={{ y: yBg }}
        className="absolute inset-0 grid-noise opacity-60 pointer-events-none"
        aria-hidden
      />
      <motion.div
        style={{ y: yBg }}
        className="absolute top-32 -right-20 h-72 w-72 bg-yellow-400/20 blur-3xl rounded-full pointer-events-none"
        aria-hidden
      />
      <motion.div
        style={{ y: yBg }}
        className="absolute -bottom-16 -left-24 h-80 w-80 bg-sky-500/10 blur-3xl rounded-full pointer-events-none"
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
        {/* Left content */}
        <motion.div
          style={{ y: yText, opacity: opacityText }}
          className="md:col-span-7 order-2 md:order-1"
        >
          {/* Live badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-900 bg-yellow-400 text-slate-950"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-slate-900 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-900" />
            </span>
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em]">
              Admissions Open · Batch 2026
            </span>
          </motion.div>

          {/* Kinetic masked headline */}
          <KineticHeadline
            data-testid="hero-headline"
            as="h1"
            delay={0.25}
            stagger={0.1}
            className="font-display mt-6 text-[2.5rem] leading-[0.98] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-black tracking-tighter text-slate-950 sm:leading-[0.94] text-balance"
            lines={[
              <>Build the skills</>,
              <>
                <span className="relative inline-block">
                  <span className="relative z-10">India</span>
                  <motion.span
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    style={{ transformOrigin: "left" }}
                    className="absolute inset-x-0 bottom-1 sm:bottom-2 h-3 sm:h-4 bg-yellow-400 -z-0"
                  />
                </span>{" "}
                actually
              </>,
              <>pays for.</>,
            ]}
          />

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-5 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            <WordReveal
              text="HWK-Koblenz, Germany certified 3.5-year diploma programs in Tool & Die Making, Precision Machining and Mechatronics — designed with Indo-German experts."
              delay={0.95}
              stagger={0.015}
            />{" "}
            <strong className="text-slate-900">
              <WordReveal text="Work in industry & earn a stipend" delay={1.4} stagger={0.02} />
            </strong>{" "}
            <WordReveal text="while you learn from Year 2." delay={1.55} stagger={0.02} />
          </motion.p>

          {/* Ticks */}
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.15 }}
            className="mt-6 grid sm:grid-cols-2 gap-2.5 max-w-xl"
          >
            {[
              "German Chamber of Skilled Crafts, Koblenz, Germany certified",
              "Earn While You Learn from Year 2",
              "Class 10 / 12 pass eligible (any stream)",
              "World-class practical with machines from India, Japan, Germany, USA etc.",
            ].map((t, i) => (
              <motion.li
                key={t}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 + i * 0.08 }}
                className="flex items-start gap-2 text-sm sm:text-[15px] text-slate-700"
              >
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{t}</span>
              </motion.li>
            ))}
          </motion.ul>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.5 }}
            className="mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3"
          >
            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href="#apply"
              data-testid="hero-apply-button"
              className="btn-yellow inline-flex items-center justify-center gap-2 px-5 sm:px-7 h-12 sm:h-14 text-sm sm:text-base font-bold uppercase tracking-wider group shine-on-hover"
            >
              Apply for Admission
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href={`tel:${KIKI.phoneRaw}`}
              data-testid="hero-call-button"
              className="inline-flex items-center justify-center gap-2 px-5 sm:px-7 h-12 sm:h-14 text-sm sm:text-base font-bold border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
            >
              <Phone className="h-5 w-5" />
              Call {KIKI.phone}
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.85 }}
            className="mt-8 flex items-center gap-3 text-xs text-slate-500"
          >
            <Award className="h-4 w-4" />
            <span>In association with HWK Koblenz, Germany · Gurugram Campus</span>
          </motion.div>
        </motion.div>

        {/* Video with subtle parallax */}
        <motion.div
          style={{ y: yVideo }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5 relative order-1 md:order-2"
        >
          <HeroVideo />
        </motion.div>
      </div>
    </section>
  );
}
